import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import PageSEO from "@/components/PageSEO";
import FeaturedCarousel from "@/components/FeaturedCarousel";
import { locationAPI, settingsAPI } from "@/lib/api";
import { HeroSection } from "@/components/landing/HeroSection";
import { HeroSearch } from "@/components/landing/HeroSearch";
import { StatsStrip } from "@/components/landing/StatsStrip";
import { DestinationsGrid } from "@/components/landing/DestinationsGrid";
import { ExperienceGrid } from "@/components/landing/ExperienceGrid";
import { FeaturedHomestays } from "@/components/landing/FeaturedHomestays";
import { WhyBookSection } from "@/components/landing/WhyBookSection";
import { BottomCTA } from "@/components/landing/BottomCTA";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const [locations, setLocations] = useState([]);
  const [homestays, setHomestays] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState(null);
  const [stats, setStats] = useState({ homestays: 0, locations: 0, villages: 0, inquiries: 0 });

  useEffect(() => {
    const fetchData = async () => {
      try { const r = await settingsAPI.get(); setSiteSettings(r.data); } catch {}
      try {
        const r = await locationAPI.getAll();
        setLocations(r.data);
        try {
          const sr = await fetch(`${BACKEND_URL}/api/public/stats`);
          const sd = await sr.json();
          setStats({ homestays: sd.homestays || 0, locations: sd.locations || 0, villages: sd.villages || 0 });
        } catch { setStats({ homestays: 0, locations: r.data.length, villages: 0 }); }
      } catch {}
      try {
        const hr = await fetch(`${BACKEND_URL}/api/homestays?limit=30`);
        const hd = await hr.json();
        const items = Array.isArray(hd) ? hd : (hd.items || hd.homestays || []);
        const withImages = items.filter(h => h.images?.length > 0);
        withImages.sort((a, b) => {
          const scoreA = (a.average_rating || 0) * 20 + (a.review_count || 0) * 5 + (a.view_count || 0) + (a.is_featured ? 50 : 0);
          const scoreB = (b.average_rating || 0) * 20 + (b.review_count || 0) * 5 + (b.view_count || 0) + (b.is_featured ? 50 : 0);
          return scoreB - scoreA;
        });
        setHomestays(withImages.slice(0, 8));
      } catch {}
      setLocationsLoading(false);
    };
    fetchData();
  }, []);

  const siteName = siteSettings?.site_name || "Kokan Homestays";
  const metaTitle = siteSettings?.home_meta_title || `${siteName} - Find Your Perfect Homestay`;
  const metaDescription = siteSettings?.home_meta_description || siteSettings?.site_description || "Discover unique homestays across India.";
  const ogImage = siteSettings?.home_og_image || siteSettings?.default_og_image || "";
  const currentUrl = window.location.origin;

  const organizationSchema = { "@context": "https://schema.org", "@type": "Organization", "name": siteName, "url": currentUrl, "description": "Curated homestay accommodations in the Konkan region" };
  const websiteSchema = { "@context": "https://schema.org", "@type": "WebSite", "name": siteName, "url": currentUrl, "potentialAction": { "@type": "SearchAction", "target": `${currentUrl}/listings?search={search_term_string}`, "query-input": "required name=search_term_string" } };

  useEffect(() => {
    document.title = metaTitle;
    const updateMeta = (sel, content, isProp = false) => {
      let m = document.querySelector(sel);
      if (m) { m.content = content; } else if (content) {
        m = document.createElement('meta');
        if (isProp) m.setAttribute('property', sel.match(/property="([^"]+)"/)?.[1] || '');
        else m.name = sel.match(/name="([^"]+)"/)?.[1] || '';
        m.content = content; document.head.appendChild(m);
      }
    };
    updateMeta('meta[name="description"]', metaDescription);
    updateMeta('meta[name="robots"]', 'index, follow');
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement('link'); canon.rel = 'canonical'; document.head.appendChild(canon); }
    canon.href = currentUrl;
    updateMeta('meta[property="og:type"]', 'website', true);
    updateMeta('meta[property="og:url"]', currentUrl, true);
    updateMeta('meta[property="og:title"]', metaTitle, true);
    updateMeta('meta[property="og:description"]', metaDescription, true);
    if (ogImage) updateMeta('meta[property="og:image"]', ogImage, true);

    const removeOld = () => document.querySelectorAll('script[data-landing-schema="true"]').forEach(el => el.remove());
    removeOld();
    const addSchema = (d) => { const s = document.createElement('script'); s.type = 'application/ld+json'; s.setAttribute('data-landing-schema', 'true'); s.textContent = JSON.stringify(d); document.head.appendChild(s); };
    addSchema(organizationSchema);
    addSchema(websiteSchema);
  }, [metaTitle, metaDescription, currentUrl, ogImage, siteName]);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />
      <PageSEO title="Premium Homestays in Konkan" description="Discover authentic Konkan homestays along the Maharashtra coast. Book serene beach houses, heritage stays, and coastal retreats in Alibaug, Ratnagiri, Ganpatipule and more." path="/" />
      <HeroSection />
      <HeroSearch />
      <StatsStrip stats={stats} />
      <DestinationsGrid locations={locations} loading={locationsLoading} />
      <ExperienceGrid />
      <FeaturedHomestays homestays={homestays} />
      <WhyBookSection />
      <FeaturedCarousel />
      <BottomCTA />
      <Footer />
    </div>
  );
};

export default LandingPage;
